import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, NGN, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Warning } from "@phosphor-icons/react";

export default function Invest() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, ready } = useAuth();
  const nav = useNavigate();

  useEffect(() => { http.get(`/opportunities/${id}`).then(r => { setOpp(r.data); setAmount(r.data.minimum_investment); }).catch(() => {}); }, [id]);

  useEffect(() => {
    if (ready && !user) nav(`/login`, { state: { from: `/invest/${id}` } });
  }, [ready, user, id, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!opp) return;
    const amt = Number(amount);
    if (isNaN(amt) || amt < opp.minimum_investment) {
      toast.error(`Minimum investment is ${NGN(opp.minimum_investment)}`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await http.post("/investments/initiate", { opportunity_id: opp.id, amount: amt });
      toast.success("Payment account ready — transfer now to complete.");
      nav(`/payment/${data.id}`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  if (!opp) return <div className="container-wide py-24 text-slate-500">Loading…</div>;

  return (
    <div>
      <section className="mayo-grain">
        <div className="container-wide py-12 max-w-3xl">
          <Link to={`/investments/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#00b4d8] mb-6"><ArrowLeft size={16} /> Back to opportunity</Link>
          <p className="text-[#00b4d8] font-display font-bold tracking-[0.25em] text-xs">STEP 1 OF 3</p>
          <h1 className="mt-3 font-display font-black text-3xl lg:text-5xl text-[#0a1128] leading-[1.05]" data-testid="invest-title">
            How much would you like to invest?
          </h1>
          <p className="mt-3 text-slate-600">You are investing in <span className="font-semibold text-[#0a1128]">{opp.name}</span>.</p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="container-wide max-w-3xl">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-8" data-testid="invest-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <p className="text-xs text-slate-500">Minimum Investment</p>
                <p className="font-display font-bold text-2xl text-[#0a1128]">{NGN(opp.minimum_investment)}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <p className="text-xs text-slate-500">Duration</p>
                <p className="font-display font-bold text-2xl text-[#0a1128]">{opp.duration}</p>
              </div>
            </div>

            <Label className="text-base">Investment Amount (₦)</Label>
            <div className="relative mt-2">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display font-black text-2xl text-[#0a1128]">₦</span>
              <Input
                type="number" required min={opp.minimum_investment} step="1000"
                value={amount} onChange={e => setAmount(e.target.value)}
                className="pl-12 h-16 text-2xl font-display font-bold border-slate-300"
                data-testid="invest-amount-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">You can invest any amount from {NGN(opp.minimum_investment)} upwards.</p>

            <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
              <Warning size={20} className="text-amber-700 mt-0.5 shrink-0" weight="bold" />
              <p className="text-sm text-amber-900"><span className="font-semibold">Risk Reminder:</span> {opp.risk_information}</p>
            </div>

            <Button type="submit" disabled={loading} className="btn-shine mt-8 w-full bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-7 text-base" data-testid="invest-continue">
              {loading ? "Preparing your payment account…" : <>Continue to Payment <ArrowRight size={18} className="ml-2" /></>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
